<script setup lang="ts">
import { updateGoogleConsent } from '~/utils/gtm'
import { clearTrackingCookies } from '~/utils/tracking-cookies'

const visible = defineModel<boolean>('visible', { default: false })

const consent = useConsentStore()
const { t } = useI18n()

const consequenceKeys = ['item1', 'item2', 'item3', 'item4'] as const

const consequences = computed(() =>
  consequenceKeys.map((key) => t(`cookies.rejectConfirm.${key}`)),
)

function cancel() {
  visible.value = false
}

function confirm() {
  consent.reject()
  updateGoogleConsent(false)
  clearTrackingCookies()
  visible.value = false
}
</script>

<template>
  <AppConfirmDialog
    v-model:visible="visible"
    :close-aria-label="t('cookies.rejectConfirm.close')"
  >
    <template #title>{{ t('cookies.rejectConfirm.title') }}</template>
    <template #default>
      <p class="app-dialog-extra">{{ t('cookies.rejectConfirm.intro') }}</p>
      <ul class="app-dialog-list">
        <li v-for="(item, index) in consequences" :key="index">{{ item }}</li>
      </ul>
    </template>
    <template #actions>
      <button type="button" class="app-dialog-confirm" @click="confirm">
        {{ t('cookies.rejectConfirm.confirm') }}
      </button>
      <button type="button" class="app-dialog-btn app-dialog-btn--secondary" @click="cancel">
        {{ t('cookies.rejectConfirm.cancel') }}
      </button>
    </template>
  </AppConfirmDialog>
</template>

<style scoped>
.cookie-reject-dialog__intro {
  margin: 0 0 1rem;
  line-height: 1.6;
  color: var(--dark-blue);
  text-align: center;
}

.cookie-reject-dialog__list {
  margin: 0 auto 1rem;
  padding-left: 1.5rem;
  max-width: 42rem;
  color: var(--dark-blue);
  list-style-type: disc;
  list-style-position: outside;

  li {
    display: list-item;
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
}

.cookie-reject-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
  width: 100%;
}

.cookie-reject-dialog__btn {
  border-radius: 999px;
  border: none;
  padding: 0.55rem 1.2rem;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: Montserrat, ui-sans-serif, system-ui, sans-serif;
}

.cookie-reject-dialog__btn--cancel {
  background: var(--ligth-blue);
  color: var(--dark-blue);
}

.cookie-reject-dialog__btn--confirm {
  background: transparent;
  color: var(--dark-blue);
  padding: 0;
  font-weight: 600;
  text-decoration: underline;
  border-radius: 0;
}

.cookie-reject-dialog__btn--confirm:hover {
  opacity: 0.8;
}
</style>

<style>
.cookie-reject-dialog-mask {
  z-index: 10002 !important;
}

.cookie-reject-dialog-root {
  z-index: 10003 !important;
}

.cookie-reject-dialog-title {
  color: var(--accent-red) !important;
  width: 100%;
  text-align: center;
}

.cookie-reject-dialog-root .p-dialog-header {
  position: relative;
  justify-content: center;
  text-align: center;
}

.cookie-reject-dialog-root .p-dialog-header-actions {
  position: absolute;
  right: 1rem;
  top: 1rem;
}

.cookie-reject-dialog-root .cookie-reject-dialog__list {
  list-style-type: disc !important;
  list-style-position: outside;
}

.cookie-reject-dialog-root .cookie-reject-dialog__list li {
  display: list-item !important;
}
</style>
